import React from "react"
import { Space, Typography } from "antd"
import { TFunction } from "i18next"
import { renderAgeRange, renderCourseType } from "../../views/TableColumnRenderers/TableColumnRenderers"
import { InterestListItem } from "./interestListData"
import { formatTimeSlotsShort } from "./timeSlotSelection"
import InterestDetailsButton from "./InterestDetailsButton"

// Interessenten-Listen werden als zusätzliche Zeilen in die normale Kurstabelle
// gemischt (ans Ende der Gesamtliste). Statt die Kurs-Spalten in eine
// diskriminierte Union zu zwingen, tragen Interest-Zeilen einen Marker und die
// Spalten-Render werden über wrapInterestColumns() an genau EINER Stelle
// interest-fähig gemacht.
export type InterestRow = {
    id: string
    __interestList: InterestListItem
}

export function isInterestRow(record: unknown): record is InterestRow {
    return typeof record === "object" && record !== null && "__interestList" in record
}

export function buildInterestRows(lists: ReadonlyArray<InterestListItem>): Array<InterestRow> {
    // id-Prefix vermeidet jede Kollision mit einer Kurs-id (rowKey nutzt record.id).
    return lists.map((list) => ({ id: `interest-${list.id}`, __interestList: list }))
}

export const EMPTY = "–"

/**
 * Rendert die Zelle einer Interest-Zeile für die gegebene Kurs-Spalte.
 * Kursart-Name als Pill (Stil der Kurszeilen) + Kennzeichnung; Ort/Kursort
 * normal; Kursbeginn = Zeitfenster; Alter aus der Kursart; alle kurs-spezifischen Spalten leer.
 */
export function renderInterestCell(columnKey: React.Key | undefined, list: InterestListItem, t: TFunction): React.ReactNode {
    switch (columnKey) {
        case "courseType":
            return (
                <Space direction='vertical' size={2}>
                    {renderCourseType({
                        name: list.courseType.name,
                        color: list.courseType.color ?? undefined,
                        style: { minWidth: 100, whiteSpace: "nowrap" },
                    })}
                    <Typography.Text type='secondary' style={{ fontSize: "0.85rem" }}>
                        {t("interest.badge", "Interessenten-Liste") as string}
                    </Typography.Text>
                </Space>
            )
        case "city":
            return list.location.city ?? EMPTY
        case "location":
            return <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.3 }}>{list.location.name}</div>
        case "startDateTime":
            return formatTimeSlotsShort(list.timeSlots ?? []) || EMPTY
        case "age":
            return renderAgeRange(list.courseType.minAge, list.courseType.maxAge)
        case "actionColumn":
            return <InterestDetailsButton list={list} />
        // Kurs-spezifische Spalten haben für eine Interessenten-Liste keinen Wert.
        case "prefixedCourseNumber":
        case "freePlaces":
        case "coursePrice":
        case "lessonCount":
        case "includedEntranceFee":
            return EMPTY
        // restrictions, status, Kurzinfo etc. bleiben leer.
        default:
            return null
    }
}
